import React, { useMemo, ReactElement } from 'react';
import Table from '../../../components/table/table.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableRow from '../../../components/table-row/table-row.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import Widget from '../../../components/widget/widget.component';
import CheckAccess from '../../../components/check-access/check-access.component';
import VisibleField from '../../../components/visible-field/visible-field.component';
import FormFieldSelect from '../../../components/form-field-select/form-field-select.component';
import FormFieldText from '../../../components/form-field-text/form-field-text.component';
import FormFieldNumber from '../../../components/form-field-number/form-field-number.component';
import FormFieldCheckbox from '../../../components/form-field-checkbox/form-field-checkbox.component';
import MenuItemList from '../../../components/menu-item-list/menu-item-list.component';
import DealCapitalization from '../deal-capitalization/deal-capitalization.component';
import DealInvestmentMetrics from '../deal-investment-metrics/deal-investment-metrics.component';
import DealInvestmentReturns from '../deal-investment-returns/deal-investment-returns.component';
import FormatService from '../../../services/format-service';
import StructureService from '../../../services/structure-service';
import {
  DEAL_BUILDING_DETAILS_ITEM_EDIT, DEAL_COMMON_EDIT, DEAL_SOURCES_ITEM_CREATE, DEAL_SOURCES_ITEM_DELETE,
  DEAL_SOURCES_ITEM_EDIT,
} from '../../../constants/entity/permissions';
import {
  DEAL_TEMPLATE_KEY_AMOUNT, DEAL_TEMPLATE_KEY_APPRAISED_VALUE_AMOUNT, DEAL_TEMPLATE_KEY_ASKING_PRICE,
  DEAL_TEMPLATE_KEY_DEAL_CAPITALIZATION, DEAL_TEMPLATE_KEY_DEAL_INVESTMENT_RETURNS, DEAL_TEMPLATE_KEY_DEAL_TERM,
  DEAL_TEMPLATE_KEY_EQUITY_AMOUNT, DEAL_TEMPLATE_KEY_EQUITY_REQUIRED, DEAL_TEMPLATE_KEY_ESTIMATED_TDC,
  DEAL_TEMPLATE_KEY_EXTENSION_CONDITIONS, DEAL_TEMPLATE_KEY_LOAN_AMOUNT, DEAL_TEMPLATE_KEY_LOAN_TYPE,
  DEAL_TEMPLATE_KEY_PROJECTED_NOI_AMOUNT, DEAL_TEMPLATE_KEY_RFP, DEAL_TEMPLATE_KEY_TOTAL_FUNDED,
  DEAL_TEMPLATE_KEY_TOTAL_UNFUNDED,
} from '../../../constants/entity/deal-template-keys';
import { DEAL_CAPITALIZATION_CATEGORY_SOURCES } from '../../../constants/entity/deal-capitalization-categories';

const DealInvestmentDetails: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const WIDGET_TITLE_DEFAULT: string = 'Investment Info';
  const WIDGET_NAME: string = 'widget-deal-investment-details';

  const {
    isSaving,
    isSaveDisabled,
    isEditMode,
    isNewDeal,
    isCustomizeMode,
    dealData,
    dealBuildingDetailsData,
    dealCapitalizationSourcesData,
    dealCapitalizationSourcesSummaryData,
    dealCapitalizationSourceNewItems,
    dealCapitalizationSourcesItemsData,
    dealCapitalizationSourcesFormValues,
    dealInvestmentReturnsData,
    dealInvestmentReturnsNewItems,
    dealTypeList,
    pricingTypeList,
    dealLoanTypeList,
    dealHiddenFieldList,
    dealCapitalizationTypeList,
    dealCapitalizationGroupList,
    onDealCapitalizationSourceAddNewItem,
    onDealCapitalizationSourceDeleteClick,
    onDealInvestmentReturnsAddNewItem,
    onDealInvestmentReturnsDeleteClick,
    onEditModeButtonClick,
  } = props;

  const dealCapitalizationItemsTotal: number = useMemo((): number => (
    dealCapitalizationSourcesData && dealCapitalizationSourcesData.length ? dealCapitalizationSourcesData.reduce((
      itemsTotal: number, dealCapitalizationGroup: DealCapitalizationGroup,
    ): number => (
      itemsTotal + ((dealCapitalizationGroup.items && dealCapitalizationGroup.items.length) || 0)
    ), 0) : 0
  ), [dealCapitalizationSourcesData]);
  const dealInvestmentReturnsItemsTotal: number = useMemo((): number => (
    dealInvestmentReturnsData && dealInvestmentReturnsData.length ? dealInvestmentReturnsData.reduce((
      itemsTotal: number, dealInvestmentReturnsItem: DealInvestmentReturnsItem,
    ): number => (
      itemsTotal + (dealInvestmentReturnsItem.value ? 1 : 0)
    ), 0) : 0
  ), [dealInvestmentReturnsData]);
  const isEmpty: boolean = !isEditMode && !isNewDeal && (!dealData || (dealData && !dealData.typeText
    && !dealData.dealTermValue && !dealData.amount && !dealData.projectedNoiAmount && !dealData.appraisedValueAmount
    && !dealData.loanAmount && !dealData.rfp && !dealData.estimatedTdc && !dealData.equityRequired
    && !dealData.askingPrice && !dealData.loanType && !dealData.extensionConditions && !dealData.equityAmount
    && !dealData.totalFunded && !dealData.totalUnfunded && !dealCapitalizationItemsTotal
    && !dealInvestmentReturnsItemsTotal));
  const dealCommonEditPermissions: string[] = useMemo((): string[] => [DEAL_COMMON_EDIT], []);
  const dealInvestmentDetailsPermissions: string[] = useMemo((): string[] => [
    DEAL_COMMON_EDIT,
    DEAL_BUILDING_DETAILS_ITEM_EDIT,
    DEAL_SOURCES_ITEM_EDIT,
  ], []);

  const dealLoanTypeMap: Record<string, DealLoanType> = useMemo((): Record<string, DealLoanType> => (
    StructureService.getItemMap(dealLoanTypeList, 'name')
  ), [dealLoanTypeList]);

  const rfpLabel: string = dealData && FormatService.formatBooleanValue(dealData.rfp, true);
  const formPrefix: string = 'dealData.dealCommonData';

  const menuItemDealTypeList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: dealTypeList,
  }), [dealTypeList]);

  const menuItemDealLoanTypeList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: dealLoanTypeList,
  }), [dealLoanTypeList]);

  return (
    <CheckAccess permissions={dealInvestmentDetailsPermissions} isEnabled={isEditMode || isNewDeal}>
      {
        !isEmpty && (
          <Widget
            isFullWidth
            isFullHeight
            isEditMode={isEditMode}
            withEditMode={!isNewDeal}
            editPermissions={dealCommonEditPermissions}
            widgetName={WIDGET_NAME}
            defaultWidgetTitle={WIDGET_TITLE_DEFAULT}
            onEditModeButtonClick={onEditModeButtonClick}
          >
            <CheckAccess permissions={dealCommonEditPermissions}>
              <Table>
                <TableBody isPlain withZebraRows withTwoColumns withHeaderColumn>
                  {
                    ((isEditMode || isNewDeal) || (!isEditMode && !isNewDeal && dealData && dealData.typeText)) && (
                      <TableRow>
                        <TableCell>
                          Deal Type
                        </TableCell>
                        <TableCell>
                          {
                            (isEditMode || isNewDeal) ? (
                              <FormFieldSelect
                                withBlankElement
                                isDisabled={dealTypeList && !dealTypeList.length}
                                name={`${formPrefix}.type`}
                                optionsTotal={dealTypeList && dealTypeList.length}
                              >
                                {menuItemDealTypeList}
                              </FormFieldSelect>
                            ) : (dealData && dealData.typeText)
                          }
                        </TableCell>
                      </TableRow>
                    )
                  }
                  <VisibleField
                    isActive={isNewDeal}
                    fieldName={DEAL_TEMPLATE_KEY_DEAL_TERM}
                    hiddenFieldList={dealHiddenFieldList}
                  >
                    {
                      ((isEditMode || isNewDeal) || (!isEditMode && !isNewDeal && dealData
                        && dealData.dealTermValue)) && (
                        <TableRow>
                          <TableCell>
                            Deal Term
                          </TableCell>
                          <TableCell>
                            {
                              (isEditMode || isNewDeal) ? (
                                <FormFieldText name={`${formPrefix}.dealTermValue`} />
                              ) : (dealData && dealData.dealTermValue)
                            }
                          </TableCell>
                        </TableRow>
                      )
                    }
                  </VisibleField>
                  <VisibleField
                    isActive={isNewDeal}
                    fieldName={DEAL_TEMPLATE_KEY_AMOUNT}
                    hiddenFieldList={dealHiddenFieldList}
                  >
                    {
                      ((isEditMode || isNewDeal) || (!isEditMode && !isNewDeal && dealData && dealData.amount)) && (
                        <TableRow>
                          <TableCell>
                            Total Capitalization
                          </TableCell>
                          <TableCell>
                            {
                              (isEditMode || isNewDeal) ? (
                                <FormFieldNumber name={`${formPrefix}.amount`} />
                              ) : (dealData && dealData.amount)
                            }
                          </TableCell>
                        </TableRow>
                      )
                    }
                  </VisibleField>
                  <VisibleField
                    isActive={isNewDeal}
                    fieldName={DEAL_TEMPLATE_KEY_PROJECTED_NOI_AMOUNT}
                    hiddenFieldList={dealHiddenFieldList}
                  >
                    {
                      ((isEditMode || isNewDeal) || (!isEditMode && !isNewDeal && dealData
                        && dealData.projectedNoiAmount)) && (
                        <TableRow>
                          <TableCell>
                            Projected NOI
                          </TableCell>
                          <TableCell>
                            {
                              (isEditMode || isNewDeal) ? (
                                <FormFieldNumber name={`${formPrefix}.projectedNoiAmount`} />
                              ) : (dealData && dealData.projectedNoiAmount)
                            }
                          </TableCell>
                        </TableRow>
                      )
                    }
                  </VisibleField>
                  <VisibleField
                    isActive={isNewDeal}
                    fieldName={DEAL_TEMPLATE_KEY_APPRAISED_VALUE_AMOUNT}
                    hiddenFieldList={dealHiddenFieldList}
                  >
                    {
                      ((isEditMode || isNewDeal) || (!isEditMode && !isNewDeal && dealData
                        && dealData.appraisedValueAmount)) && (
                        <TableRow>
                          <TableCell>
                            Appraised Value
                          </TableCell>
                          <TableCell>
                            {
                              (isEditMode || isNewDeal) ? (
                                <FormFieldNumber name={`${formPrefix}.appraisedValueAmount`} />
                              ) : (dealData && dealData.appraisedValueAmount)
                            }
                          </TableCell>
                        </TableRow>
                      )
                    }
                  </VisibleField>
                  <VisibleField
                    isActive={isNewDeal}
                    fieldName={DEAL_TEMPLATE_KEY_LOAN_AMOUNT}
                    hiddenFieldList={dealHiddenFieldList}
                  >
                    {
                      ((isEditMode || isNewDeal) || (!isEditMode && !isNewDeal && dealData && dealData.loanAmount)) && (
                        <TableRow>
                          <TableCell>
                            Loan Amount
                          </TableCell>
                          <TableCell>
                            {
                              (isEditMode || isNewDeal) ? (
                                <FormFieldNumber name={`${formPrefix}.loanAmount`} />
                              ) : (dealData && dealData.loanAmount)
                            }
                          </TableCell>
                        </TableRow>
                      )
                    }
                  </VisibleField>
                  <VisibleField
                    isActive={isNewDeal}
                    fieldName={DEAL_TEMPLATE_KEY_RFP}
                    hiddenFieldList={dealHiddenFieldList}
                  >
                    {
                      ((isEditMode || isNewDeal) || (!isEditMode && !isNewDeal && dealData && !!dealData.rfp)) && (
                        <TableRow>
                          <TableCell>
                            RFP
                          </TableCell>
                          <TableCell>
                            {
                              (isEditMode || isNewDeal) ? (
                                <FormFieldCheckbox name={`${formPrefix}.rfp`} />
                              ) : rfpLabel
                            }
                          </TableCell>
                        </TableRow>
                      )
                    }
                  </VisibleField>
                  <VisibleField
                    isActive={isNewDeal}
                    fieldName={DEAL_TEMPLATE_KEY_ESTIMATED_TDC}
                    hiddenFieldList={dealHiddenFieldList}
                  >
                    {
                      ((isEditMode || isNewDeal) || (!isEditMode && !isNewDeal && dealData
                        && dealData.estimatedTdc)) && (
                        <TableRow>
                          <TableCell>
                            Estimated TDC
                          </TableCell>
                          <TableCell>
                            {
                              (isEditMode || isNewDeal) ? (
                                <FormFieldNumber name={`${formPrefix}.estimatedTdc`} />
                              ) : (dealData && dealData.estimatedTdc)
                            }
                          </TableCell>
                        </TableRow>
                      )
                    }
                  </VisibleField>
                  <VisibleField
                    isActive={isNewDeal}
                    fieldName={DEAL_TEMPLATE_KEY_EQUITY_REQUIRED}
                    hiddenFieldList={dealHiddenFieldList}
                  >
                    {
                      ((isEditMode || isNewDeal) || (!isEditMode && !isNewDeal && dealData
                        && dealData.equityRequired)) && (
                        <TableRow>
                          <TableCell>
                            Equity Required
                          </TableCell>
                          <TableCell>
                            {
                              (isEditMode || isNewDeal) ? (
                                <FormFieldNumber name={`${formPrefix}.equityRequired`} />
                              ) : (dealData && dealData.equityRequired)
                            }
                          </TableCell>
                        </TableRow>
                      )
                    }
                  </VisibleField>
                  <VisibleField
                    isActive={isNewDeal}
                    fieldName={DEAL_TEMPLATE_KEY_ASKING_PRICE}
                    hiddenFieldList={dealHiddenFieldList}
                  >
                    {
                      ((isEditMode || isNewDeal) || (!isEditMode && !isNewDeal && dealData
                        && dealData.askingPrice)) && (
                        <TableRow>
                          <TableCell>
                            Asking Price
                          </TableCell>
                          <TableCell>
                            {
                              (isEditMode || isNewDeal) ? (
                                <FormFieldNumber name={`${formPrefix}.askingPrice`} />
                              ) : (dealData && dealData.askingPrice)
                            }
                          </TableCell>
                        </TableRow>
                      )
                    }
                  </VisibleField>
                  <VisibleField
                    isActive={isNewDeal}
                    fieldName={DEAL_TEMPLATE_KEY_LOAN_TYPE}
                    hiddenFieldList={dealHiddenFieldList}
                  >
                    {
                      ((isEditMode || isNewDeal) || (!isEditMode && !isNewDeal && dealData && dealData.loanType)) && (
                        <TableRow>
                          <TableCell>
                            Loan Type
                          </TableCell>
                          <TableCell>
                            {
                              (isEditMode || isNewDeal) ? (
                                <FormFieldSelect
                                  withBlankElement
                                  isDisabled={dealLoanTypeList && !dealLoanTypeList.length}
                                  name={`${formPrefix}.loanType`}
                                  optionsTotal={dealLoanTypeList && dealLoanTypeList.length}
                                >
                                  {menuItemDealLoanTypeList}
                                </FormFieldSelect>
                              ) : (
                                (dealLoanTypeMap[dealData.loanType]
                                  && dealLoanTypeMap[dealData.loanType].title) || ''
                              )
                            }
                          </TableCell>
                        </TableRow>
                      )
                    }
                  </VisibleField>
                  <VisibleField
                    isActive={isNewDeal}
                    fieldName={DEAL_TEMPLATE_KEY_EXTENSION_CONDITIONS}
                    hiddenFieldList={dealHiddenFieldList}
                  >
                    {
                      ((isEditMode || isNewDeal) || (!isEditMode && !isNewDeal && dealData
                        && dealData.extensionConditions)) && (
                        <TableRow>
                          <TableCell>
                            Extension Conditions
                          </TableCell>
                          <TableCell>
                            {
                              (isEditMode || isNewDeal) ? (
                                <FormFieldText name={`${formPrefix}.extensionConditions`} />
                              ) : (dealData && dealData.extensionConditions)
                            }
                          </TableCell>
                        </TableRow>
                      )
                    }
                  </VisibleField>
                  <VisibleField
                    isActive={isNewDeal}
                    fieldName={DEAL_TEMPLATE_KEY_EQUITY_AMOUNT}
                    hiddenFieldList={dealHiddenFieldList}
                  >
                    {
                      ((isEditMode || isNewDeal) || (!isEditMode && !isNewDeal && dealData
                        && dealData.equityAmount)) && (
                        <TableRow>
                          <TableCell>
                            Equity Amount
                          </TableCell>
                          <TableCell>
                            {
                              (isEditMode || isNewDeal) ? (
                                <FormFieldNumber name={`${formPrefix}.equityAmount`} />
                              ) : (dealData && dealData.equityAmount)
                            }
                          </TableCell>
                        </TableRow>
                      )
                    }
                  </VisibleField>
                  <VisibleField
                    isActive={isNewDeal}
                    fieldName={DEAL_TEMPLATE_KEY_TOTAL_FUNDED}
                    hiddenFieldList={dealHiddenFieldList}
                  >
                    {
                      ((isEditMode || isNewDeal) || (!isEditMode && !isNewDeal && dealData
                        && dealData.totalFunded)) && (
                        <TableRow>
                          <TableCell>
                            Total Funded
                          </TableCell>
                          <TableCell>
                            {
                              (isEditMode || isNewDeal) ? (
                                <FormFieldNumber name={`${formPrefix}.totalFunded`} />
                              ) : (dealData && dealData.totalFunded)
                            }
                          </TableCell>
                        </TableRow>
                      )
                    }
                  </VisibleField>
                  <VisibleField
                    isActive={isNewDeal}
                    fieldName={DEAL_TEMPLATE_KEY_TOTAL_UNFUNDED}
                    hiddenFieldList={dealHiddenFieldList}
                  >
                    {
                      ((isEditMode || isNewDeal) || (!isEditMode && !isNewDeal && dealData
                        && dealData.totalUnfunded)) && (
                        <TableRow>
                          <TableCell>
                            Total Unfunded
                          </TableCell>
                          <TableCell>
                            {
                              (isEditMode || isNewDeal) ? (
                                <FormFieldNumber name={`${formPrefix}.totalUnfunded`} />
                              ) : (dealData && dealData.totalUnfunded)
                            }
                          </TableCell>
                        </TableRow>
                      )
                    }
                  </VisibleField>
                </TableBody>
              </Table>
            </CheckAccess>
            <VisibleField
              isActive={isNewDeal}
              fieldName={DEAL_TEMPLATE_KEY_DEAL_CAPITALIZATION}
              hiddenFieldList={dealHiddenFieldList}
            >
              <DealCapitalization
                isSimpleView
                isSaving={isSaving}
                isSaveDisabled={isSaveDisabled}
                isEditMode={isEditMode}
                isNewDeal={isNewDeal}
                isCustomizeMode={isCustomizeMode}
                dealCapitalizationCategory={DEAL_CAPITALIZATION_CATEGORY_SOURCES}
                dealCapitalizationData={dealCapitalizationSourcesData}
                dealCapitalizationSummaryData={dealCapitalizationSourcesSummaryData}
                dealCapitalizationNewItems={dealCapitalizationSourceNewItems}
                dealCapitalizationItemsData={dealCapitalizationSourcesItemsData}
                groupTitle="Source"
                commonTitle="Capitalization"
                createItemPermission={DEAL_SOURCES_ITEM_CREATE}
                editItemPermission={DEAL_SOURCES_ITEM_EDIT}
                deleteItemPermission={DEAL_SOURCES_ITEM_DELETE}
                dealCapitalizationTypeList={dealCapitalizationTypeList}
                dealCapitalizationGroupList={dealCapitalizationGroupList}
                onAddNewItem={onDealCapitalizationSourceAddNewItem}
                onDeleteClick={onDealCapitalizationSourceDeleteClick}
              />
            </VisibleField>
            <VisibleField
              isActive={isNewDeal}
              fieldName={DEAL_TEMPLATE_KEY_DEAL_CAPITALIZATION}
              hiddenFieldList={dealHiddenFieldList}
            >
              <DealInvestmentMetrics
                isEditMode={isEditMode}
                isNewDeal={isNewDeal}
                dealBuildingDetailsData={dealBuildingDetailsData}
              />
            </VisibleField>
            <VisibleField
              isActive={isNewDeal}
              fieldName={DEAL_TEMPLATE_KEY_DEAL_INVESTMENT_RETURNS}
              hiddenFieldList={dealHiddenFieldList}
            >
              <DealInvestmentReturns
                isEditMode={isEditMode}
                isNewDeal={isNewDeal}
                dealData={dealData}
                dealCapitalizationData={dealCapitalizationSourcesData}
                dealCapitalizationCategory={DEAL_CAPITALIZATION_CATEGORY_SOURCES}
                dealCapitalizationNewItems={dealCapitalizationSourceNewItems}
                dealCapitalizationFormValues={dealCapitalizationSourcesFormValues}
                dealInvestmentReturnsData={dealInvestmentReturnsData}
                dealInvestmentReturnsNewItems={dealInvestmentReturnsNewItems}
                pricingTypeList={pricingTypeList}
                dealCapitalizationTypeList={dealCapitalizationTypeList}
                dealCapitalizationGroupList={dealCapitalizationGroupList}
                onAddNewItem={onDealInvestmentReturnsAddNewItem}
                onDeleteClick={onDealInvestmentReturnsDeleteClick}
              />
            </VisibleField>
          </Widget>
        )
      }
    </CheckAccess>
  );
});

export default DealInvestmentDetails;
